import {alertVariants} from "@visiblethread/react-components";
import localhost from "apis/localhost";
import {modalTypes} from "constants/types";
import {AI_MODAL_PROPERTIES} from "layout/systemAdmin/GenerativeAI/constants";
import {showErrorModal} from "slices/app/actions/error";
import {hideModal, showModal} from "slices/app/actions/modals";
import {addToJobQueue, hideGlobalAlert, removeFromJobQueue, showGlobalAlert,} from "slices/app/appSlice";
import {getErrorMessage} from "utils";

import {
  INITIAL_AI_SETTINGS,
  resetEdits,
  setAnalysisLlm,
  setAreAIConnectionsTested,
  setEdits,
  setGenerativeAISettings,
  setIsAiEnabledInitialized,
  setIsLlmAvailable,
  updateGenerativeAISettings,
} from "./generativeAiSlice";
import {selectGenerativeAiSettings} from "./selectors";

const ANALYSIS_LLM_ENABLED_URL = "/genAiSettings/analysis-llm/enabled";
const VISI_CHAT_ENABLED_URL = "/genAiSettings/ai/enabled";

const onBeforeUnload = (e) => {
  e.preventDefault();
  e.returnValue = true;
};

export const addBeforeUnload = () => {
  window.addEventListener("beforeunload", onBeforeUnload);
  return { type: "ADD_BEFORE_UNLOAD" };
};

export const removeBeforeUnload = () => {
  window.removeEventListener("beforeunload", onBeforeUnload);
  return { type: "REMOVE_BEFORE_UNLOAD" };
};

const CONNECTION_FAILED = "Connection failed";

// A response means the server was reached, so the failure is a validation/permission/server
// error — not a lost connection. Only label it "Connection failed" when no response came back.
const resolveSaveErrorTitle = (error) =>
  error?.response ? "Unable to Save Settings" : CONNECTION_FAILED;

export const setFormEdits = (data) => {
  return (dispatch, getState) => {
    const { generativeAi } = getState();
    const { edits = {} } = generativeAi || {};
    const isDirty = Boolean(Object.keys(edits).length);
    dispatch(setEdits(data));
    if (!isDirty) {
      dispatch(addBeforeUnload());
    }
  };
};

const filterEmptyStrings = (obj) => {
  const filtered = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== "" && value !== undefined && value !== null) {
      if (
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        const nestedFiltered = filterEmptyStrings(value);
        if (Object.keys(nestedFiltered).length > 0) {
          filtered[key] = nestedFiltered;
        }
      } else {
        filtered[key] = value;
      }
    }
  }
  return filtered;
};

const filterValidFields = (obj) => {
  const params = JSON.parse(JSON.stringify(obj));

  const framework = params.framework;
  const validFields = AI_MODAL_PROPERTIES[framework] || [];

  if (!params.settings) {
    return params;
  }

  const validSettingKeys = validFields
    .filter((field) => field.startsWith("settings."))
    .map((field) => field.replace("settings.", ""));

  Object.keys(params.settings).forEach((key) => {
    if (!validSettingKeys.includes(key)) {
      delete params.settings[key];
    }
  });

  return params;
};

export const fetchIsGenerativeAiEnabled = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("fetchIsGenerativeAIEnabled"));
  try {
    const { data } = await localhost.get("/genAi/enabled");

    if (data) {
      const aiSettings = selectGenerativeAiSettings(getState());
      dispatch(
        setGenerativeAISettings({
          ...aiSettings,
          isEnabled: data.enabled,
          isRagEnabled: data.ragEnabled,
          isSharepointDownloadEnabled: data.sharepointDownloadEnabled,
        })
      );
    }
  } catch (error) {
    const { response } = error;
    const errorMessage = response?.data?.detail || "Failed to fetch ai enabled";
    error.message = errorMessage;
    dispatch(showErrorModal(error, CONNECTION_FAILED));
  } finally {
    dispatch(removeFromJobQueue("fetchIsGenerativeAIEnabled"));
    dispatch(setIsAiEnabledInitialized());
  }
};

export const fetchAnalysisLlmEnabled = () => async (dispatch) => {
  try {
    const { data } = await localhost.get("/genAi/analysis-llm/enabled");
    dispatch(setIsLlmAvailable(data?.enabled ?? false));
  } catch (error) {
    dispatch(setIsLlmAvailable(false));
  }
};

export const fetchAnalysisLlmInfo = () => {
  return async (dispatch) => {
    try {
      dispatch(addToJobQueue("fetchAnalysisLlm"));
      const { data } = await localhost.get("/genAiSettings/analysis-llm");
      dispatch(setAnalysisLlm(data || null));
      dispatch(setIsLlmAvailable(data?.enabled ?? false));
    } catch (error) {
      const { response } = error;
      const errorMessage =
        response?.data?.detail || "Failed to fetch analysis LLM info";
      dispatch(showErrorModal({ message: errorMessage }, CONNECTION_FAILED));
    } finally {
      dispatch(removeFromJobQueue("fetchAnalysisLlm"));
    }
  };
};

export const fetchGenerativeAiSettings = ({ isOnPremise = true }) => {
  return async (dispatch) => {
    dispatch(addToJobQueue("fetchGenerativeAiSettings"));

    try {
      const endpoint = isOnPremise
        ? "/genAiSettings/ai"
        : "/genAiSettings/cloud/ai";

      const { data } = await localhost.get(endpoint);

      if (data) {
        const defaultSnippetCount = data.defaultRetrievalSnippetCount || 0;
        const ragSettings = data.ragSettings
          ? {
              ...data.ragSettings,
              // Config-pinned tenants report sharingEnabled straight from
              // vt.llm.visi-chat.default.rag.sharingEnabled, independent of the resolved
              // ragEnabled state — never show sharing as on when Collections itself is off.
              isLocalFileSharingEnabled:
                Boolean(data.ragEnabled) &&
                Boolean(data.ragSettings?.sharingEnabled),
              defaultRetrievalSnippetCount: defaultSnippetCount,
            }
          : {
              retrievalSnippetCount: defaultSnippetCount,
              isLocalFileSharingEnabled: false,
              defaultRetrievalSnippetCount: defaultSnippetCount,
            };

        const settings = {
          id: data.id,
          isEnabled: data.enabled,
          isRagEnabled: data.ragEnabled,
          authenticationMethod: data.settings?.authenticationMethod,
          ragSettings,
          genAiManagedByConfig: Boolean(data.genAiManagedByConfig),
          ragManagedByConfig: Boolean(data.ragManagedByConfig),
        };

        if (isOnPremise) {
          settings.framework = data.framework;
          settings.settings = data.settings;
          settings.isStreamingEnabled = data.streaming;
        }

        dispatch(setGenerativeAISettings(settings));
      }
    } catch (error) {
      const { response } = error;
      const errorMessage = response?.data?.detail || "Failed to fetch settings";
      dispatch(showErrorModal({ message: errorMessage }, CONNECTION_FAILED));
    } finally {
      dispatch(removeFromJobQueue("fetchGenerativeAiSettings"));
    }
  };
};

export const setAnalysisAiEnabled = (enabled) => {
  return async (dispatch) => {
    try {
      await localhost.put(ANALYSIS_LLM_ENABLED_URL, { enabled });
      await dispatch(fetchAnalysisLlmInfo());
    } catch (error) {
      const { response } = error;
      const errorMessage =
        response?.data?.detail || "Failed to update analysis LLM setting";
      dispatch(showErrorModal({ message: errorMessage }, "Connection failed"));
    }
  };
};

export const setVisiChatAiEnabled = (enabled) => {
  return async (dispatch) => {
    try {
      await localhost.put(VISI_CHAT_ENABLED_URL, { enabled });
      await dispatch(fetchGenerativeAiSettings({ isOnPremise: true }));
    } catch (error) {
      const { response } = error;
      const errorMessage =
        response?.data?.detail || "Failed to update generative AI setting";
      dispatch(showErrorModal({ message: errorMessage }, "Connection failed"));
    }
  };
};

const buildAiSettingsParams = ({ settings, isOnPremise, isGovMode = false }) => {
  const params = { enabled: settings.isEnabled, id: settings.id };

  if (settings.isRagEnabled) {
    params.ragEnabled = settings.isRagEnabled;
  }

  // Gov sends the cloud-shaped payload — decisions only. The backend sources every connection
  // field from vt.llm.visi-chat.default.*, so sending them would be pointless and misleading.
  if (isOnPremise && !isGovMode) {
    if (settings.isEnabled) {
      const configParams = {
        id: settings.id,
        framework: settings.framework,
        settings: settings.settings,
        streaming: settings.isStreamingEnabled,
        ragEnabled: settings.isRagEnabled,
      };
      Object.assign(
        params,
        filterValidFields(filterEmptyStrings(configParams))
      );
    }

    if (settings.isRagEnabled) {
      const ragSettings = settings.ragSettings;
      const ragParams = {
        ragSettings: {
          endpoint: ragSettings.endpoint,
          retrievalSnippetCount: ragSettings.retrievalSnippetCount,
          sharingEnabled: ragSettings.isLocalFileSharingEnabled,
        },
      };
      Object.assign(params, filterEmptyStrings(ragParams));
    }
  } else {
    params.id = settings.id;
    if (settings.isRagEnabled) {
      params.ragSettings = {
        sharingEnabled: settings.ragSettings.isLocalFileSharingEnabled,
      };
    }
  }

  return params;
};

export const testGenerativeAiSettings = ({ settings, isOnPremise = true }) => {
  return async (dispatch) => {
    dispatch(addToJobQueue("testGenerativeAiSettings"));

    try {
      const params = buildAiSettingsParams({ settings, isOnPremise });
      const endpoint = isOnPremise
        ? "/genAiSettings/ai/test"
        : "/genAiSettings/cloud/ai/test";

      await localhost.post(endpoint, params);

      dispatch(
        showModal(modalTypes.CONFIRMATION, {
          headerText: "Test Generative AI Configuration Connection",
          bodyText: "Success - The connection was successful.",
          okButton: {
            text: "Ok",
            onOk: () => {
              dispatch(hideModal(modalTypes.CONFIRMATION));
            },
          },
        })
      );

      dispatch(setAreAIConnectionsTested(true));
    } catch (error) {
      const errorMessage = getErrorMessage(error) || "Please check your settings.";
      dispatch(showErrorModal({ message: errorMessage }, CONNECTION_FAILED));
    } finally {
      dispatch(removeFromJobQueue("testGenerativeAiSettings"));
    }
  };
};

export const saveGenerativeAiSettings = ({
  settings,
  isOnPremise = true,
  isGovMode = false,
}) => {
  return async (dispatch) => {
    dispatch(addToJobQueue("saveGenerativeAiSettings"));
    // Clear any residual banner (e.g. a prior "saved successfully") so it can't linger
    // alongside an error modal from this attempt.
    dispatch(hideGlobalAlert());
    try {
      if (isGovMode) {
        const params = buildAiSettingsParams({ settings, isOnPremise, isGovMode });
        await localhost.post("/genAiSettings/ai/save", params);
        await dispatch(setVisiChatAiEnabled(settings.isEnabled));
      } else if (settings.isEnabled) {
        const params = buildAiSettingsParams({
          settings,
          isOnPremise,
          isGovMode,
        });

        const endpoint = isOnPremise
          ? "/genAiSettings/ai/save"
          : "/genAiSettings/cloud/ai/save";

        const { data } = await localhost.post(endpoint, params);
        dispatch(updateGenerativeAISettings({ ...settings, id: data.id }));
      } else if (settings.id) {
        const endpoint = isOnPremise
          ? "/genAiSettings/ai/disable"
          : "/genAiSettings/cloud/ai/disable";

        await localhost.delete(endpoint, {
          params: { id: settings.id },
        });
        dispatch(setGenerativeAISettings(INITIAL_AI_SETTINGS));
      } else {
        // No persisted record yet: the feature is already disabled, so there is nothing to
        // delete. Skip the call to avoid sending a null id (VT-9139).
        dispatch(setGenerativeAISettings(INITIAL_AI_SETTINGS));
      }

      dispatch(resetEdits());
      dispatch(removeBeforeUnload());
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: "Settings saved successfully",
        })
      );
    } catch (error) {
      const errorMessage = getErrorMessage(error) || "Please check your settings.";
      dispatch(
        showErrorModal({ message: errorMessage }, resolveSaveErrorTitle(error))
      );
    } finally {
      dispatch(removeFromJobQueue("saveGenerativeAiSettings"));
    }
  };
};
